import { useCallback, useMemo } from 'react';
import { Image, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { TimelineItem, ImageItem } from '@/types/timeline';
import { useTimelineStore } from '@/features/timeline/stores/timeline-store';
import type { TimelineState, TimelineActions } from '@/features/timeline/types';
import {
  PropertySection,
  PropertyRow,
  NumberInput,
} from '../components';
import { getMixedValue } from '../utils';
import { useTranslation } from '@/features/i18n/locales';

// Speed limits (matching rate-stretch)
const MIN_SPEED = 0.1;
const MAX_SPEED = 10.0;

interface GifSectionProps {
  items: TimelineItem[];
}

/**
 * Check if an image item is a GIF based on its label
 */
function isGifItem(item: TimelineItem): item is ImageItem {
  return item.type === 'image' && (item.label?.toLowerCase().endsWith('.gif') ?? false);
}

/**
 * GIF section - playback speed for animated GIFs.
 * Only shown when selection includes GIF clips.
 *
 * Unlike videos, GIF speed changes don't affect duration (GIFs loop):
 * - Faster speed = animation plays faster within same duration
 * - Slower speed = animation plays slower within same duration
 */
export function GifSection({ items }: GifSectionProps) {
  const t = useTranslation();
  const rateStretchItem = useTimelineStore((s: TimelineState & TimelineActions) => s.rateStretchItem);

  const gifItems = useMemo(
    () => items.filter(isGifItem),
    [items]
  );

  // Memoize item IDs for stable callback dependencies
  const itemIds = useMemo(() => gifItems.map((item) => item.id), [gifItems]);

  // Get current speed (defaults to 1)
  const speed = getMixedValue(gifItems, (item) => item.speed, 1);

  // Handle speed change - for GIFs, duration stays the same, only speed changes
  const handleSpeedChange = useCallback(
    (newSpeed: number) => {
      // Round to 2 decimal places to match clip label precision and avoid floating point drift
      const roundedSpeed = Math.round(newSpeed * 100) / 100;
      // Clamp speed to valid range
      const clampedSpeed = Math.max(MIN_SPEED, Math.min(MAX_SPEED, roundedSpeed));

      const currentItems = useTimelineStore.getState().items;
      currentItems
        .filter((item: TimelineItem): item is ImageItem => isGifItem(item) && itemIds.includes(item.id))
        .forEach((item: ImageItem) => {
          // For GIFs, duration stays the same, only speed changes
          rateStretchItem(item.id, item.from, item.durationInFrames, clampedSpeed);
        });
    },
    [itemIds, rateStretchItem]
  );

  // Reset speed to 1x
  const handleResetSpeed = useCallback(() => {
    const tolerance = 0.01;
    const currentItems = useTimelineStore.getState().items;
    currentItems
      .filter((item: TimelineItem): item is ImageItem => isGifItem(item) && itemIds.includes(item.id))
      .forEach((item: ImageItem) => {
        const currentSpeed = item.speed || 1;
        if (Math.abs(currentSpeed - 1) <= tolerance) return;

        // Reset to 1x speed, keep duration the same
        rateStretchItem(item.id, item.from, item.durationInFrames, 1);
      });
  }, [itemIds, rateStretchItem]);

  if (gifItems.length === 0) return null;

  return (
    <PropertySection title={t.properties.gif} icon={Image} defaultOpen={true}>
      {/* Playback Speed - affects animation rate (not duration) */}
      <PropertyRow label={t.properties.speed}>
        <div className="flex items-center gap-1 w-full">
          <NumberInput
            value={speed}
            onChange={handleSpeedChange}
            min={MIN_SPEED}
            max={MAX_SPEED}
            step={0.1}
            unit="x"
            className="flex-1 min-w-0"
          />
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 flex-shrink-0"
            onClick={handleResetSpeed}
            title={t.properties.resetToOne}
          >
            <RotateCcw className="w-3.5 h-3.5" />
          </Button>
        </div>
      </PropertyRow>
    </PropertySection>
  );
}
